import { useState, type ReactNode } from 'react';
import { AlarmClock, Check, ChevronDown } from 'lucide-react';
import { Button, InfoHint, Select } from '../../components/ui';
import { cn } from '../../lib/cn';
import {
  CADENCE_LABEL,
  formatNextRun,
  formatSentAt,
  HOUR_OPTIONS,
  PERIOD_NOUN,
  useReminders,
  useUpdateReminders,
  WEEKDAY_OPTIONS,
  type ChasedCadence,
  type ReminderOverview,
} from './reminders';

/**
 * **The line that replaced a weekly chore** — now for three frequencies.
 *
 * A timesheet is a PERIOD per person (a day, a week or a month), so "Monday
 * 9:00 AM" is no longer the whole answer: a daily submitter is chased every
 * workday, a monthly one on the first workday of the month and then weekly
 * until it is in. The server writes one sentence per frequency
 * (`cadenceSchedule`) with the same function the cron obeys; this bar prints
 * the ones somebody on the team actually uses, and nothing it would have to
 * compute itself.
 *
 * - **ON by default, no setup.** The schedule editor is a collapsed
 *   disclosure — the default is right for almost everybody.
 * - **The delivery sentence sits beside the button, before it is pressed.**
 *   In `EMAIL_MODE=file` nothing is transmitted and the bar says so; a
 *   reminder that claims to have gone out when it was written to disk is the
 *   failure the invoice composer's same rule exists to stop.
 */

export interface ReminderBarProps {
  /** Server-decided: only an approver may see or manage reminders. */
  canApprove: boolean;
  /** The frequencies somebody on the team submits at — only these are printed. */
  cadences: ChasedCadence[];
  /** The bulk "Remind N" button, owned by the queue that knows who is open. */
  action?: ReactNode;
}

const ORDER: ChasedCadence[] = ['DAILY', 'WEEKLY', 'MONTHLY'];

export function ReminderBar({ canApprove, cadences, action }: ReminderBarProps) {
  const reminders = useReminders(canApprove);
  const update = useUpdateReminders();
  const [editing, setEditing] = useState(false);

  if (!canApprove) return null;
  // A reminder block that cannot load must not become a broken promise: the
  // queue keeps working and simply says nothing about reminders.
  if (reminders.isPending || reminders.isError || !reminders.data) return null;

  const data: ReminderOverview = reminders.data;
  const { settings } = data;
  const nextRun = formatNextRun(data.nextRunAt, settings.timeZone);
  const lastRun = formatSentAt(data.lastRunAt, settings.timeZone);
  const inUse = ORDER.filter((c) => cadences.includes(c));
  const filedOnly = data.email.mode !== 'live';

  return (
    <div className="border-b border-grey-100 px-4 py-3">
      <div className="flex flex-wrap items-start gap-x-3 gap-y-2">
        <AlarmClock
          className={cn(
            'mt-0.5 h-4 w-4 shrink-0',
            settings.enabled ? 'text-accent' : 'text-grey-400',
          )}
          aria-hidden
        />
        <div className="min-w-0 flex-1 basis-[calc(100%-2rem)] sm:basis-0">
          {settings.enabled ? (
            <>
              <div className="text-body-sm text-grey-600">
                Reminders go out <span className="font-bold text-secondary">automatically</span>
                <InfoHint label="Automatic reminders" className="ml-1" width={320}>
                  {nextRun && (
                    <p>
                      Next run <span className="font-semibold text-secondary">{nextRun}</span>.
                    </p>
                  )}
                  {lastRun && <p className={nextRun ? 'mt-2' : undefined}>Last run {lastRun}.</p>}
                  <p className="mt-2">
                    A reminder is about one period — the day, week or month that person submits.
                    Remind now sends the same message the scheduled run sends, and lands in the
                    same log.
                  </p>
                  <p className="mt-2">
                    Reminders reach back {data.chaseWindow.DAY} days, {data.chaseWindow.WEEK}{' '}
                    weeks or {data.chaseWindow.MONTH} months. Anything older is a conversation,
                    not a nudge — nothing is sent for it.
                  </p>
                </InfoHint>
              </div>
              {inUse.length > 0 ? (
                <ul className="mt-1 space-y-0.5 text-caption text-grey-500">
                  {inUse.map((c) => (
                    <li key={c}>
                      <span className="font-semibold text-secondary">{CADENCE_LABEL[c]}</span> ·{' '}
                      {data.cadenceSchedule[c]}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-1 text-caption text-grey-500">
                  Nobody has a submission frequency, so nobody is chased.
                </p>
              )}
            </>
          ) : (
            <p className="text-body-sm text-grey-600">
              <span className="font-bold text-secondary">Automatic reminders are off.</span>{' '}
              Nobody is chased unless you remind them here.
            </p>
          )}
        </div>

        <div className="flex w-full shrink-0 items-center gap-2 sm:ml-auto sm:w-auto">
          <Button
            variant="ghost"
            size="sm"
            aria-expanded={editing}
            onClick={() => setEditing((v) => !v)}
          >
            Schedule
            <ChevronDown
              className={cn('h-3.5 w-3.5 transition-transform', editing && 'rotate-180')}
              aria-hidden
            />
          </Button>
          {action}
        </div>
      </div>

      {/* The honest line about delivery, ABOVE nothing and beside the button —
          read before anything is pressed. */}
      {filedOnly && (
        <p className="mt-1.5 text-caption text-grey-500 sm:pl-7">
          {data.email.mode === 'off'
            ? 'Outbound email is switched off on this server — nothing is written or sent.'
            : `Development mode — ${data.email.reason ?? 'nothing is transmitted.'}`}
        </p>
      )}

      {editing && (
        <div className="mt-2.5 flex flex-wrap items-end gap-3 rounded-lg border border-grey-200 bg-grey-50 px-3 py-2.5">
          <label className="flex items-center gap-2 pb-1.5 text-body-sm font-semibold text-secondary">
            <input
              type="checkbox"
              checked={settings.enabled}
              onChange={(e) => update.mutate({ enabled: e.target.checked })}
              className="h-4 w-4 cursor-pointer rounded border-grey-300 text-accent focus:ring-accent/30"
            />
            Send automatically
          </label>
          <Select
            className="w-36"
            label="Day"
            selectSize="sm"
            value={String(settings.weekday)}
            disabled={!settings.enabled}
            options={WEEKDAY_OPTIONS}
            onChange={(e) => update.mutate({ weekday: Number(e.target.value) })}
          />
          <Select
            className="w-32"
            label="Time"
            selectSize="sm"
            value={String(settings.hour)}
            disabled={!settings.enabled}
            options={HOUR_OPTIONS}
            onChange={(e) => update.mutate({ hour: Number(e.target.value) })}
          />
          {/* The zone is a FACT about the org, not a fourth control: "9:00 AM"
              means 9 AM where the team is, DST included. */}
          <div className="pb-1.5 text-caption text-grey-500">
            {settings.timeZone.replace(/_/g, ' ')}
            <InfoHint label="What the day and time decide" className="ml-1" width={300}>
              <p>
                The time applies to every reminder. The day is when weekly {PERIOD_NOUN.WEEK}s
                are chased, and when a {PERIOD_NOUN.MONTH} still not in is chased again. Daily
                submitters are chased every workday at this time.
              </p>
            </InfoHint>
          </div>
          {update.isSuccess && !update.isPending && (
            <p className="flex items-center gap-1 pb-1.5 text-caption font-semibold text-status-paid">
              <Check className="h-3.5 w-3.5" aria-hidden />
              Saved
            </p>
          )}
        </div>
      )}
    </div>
  );
}
